import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import {
	BooleanField,
	CurrencyTextField,
	DateField,
	Form,
	ModuleSearchField,
	NumberTextField,
	SearchSelectField,
	useModel,
	useSettings,
	useUserData,
	Utils
} from 'app/main/components';

interface ItemsPricingsEditDialogProps {
	baseModel: any;
	isAdd?: boolean;
	updateRefresh: VoidFunction;
}

export function showItemsPricingsEditDialog(props: ItemsPricingsEditDialogProps) {
	return openDialog({
		children: <ItemsPricingsEditDialog {...props} />,
		maxWidth: 'xs',
		fullWidth: true
	});
}

export default function ItemsPricingsEditDialog(props: ItemsPricingsEditDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('items_pricings');
	const userData = useUserData();

	const { model, modifiedModel, sendChange, save } = useModel({
		baseModel: props.baseModel,
		settings,
		isEdit: true,
		isAdd: props.isAdd
	});

	function successHandler() {
		dispatch(Utils.alertSuccessMsg(t));
		dispatch(closeDialog());
		props.updateRefresh();
	}

	function updateLine() {
		if (!props.isAdd) save({ onSuccess: successHandler });
		else {
			dispatch(Utils.alertProgressMsg(t));
			Utils.executeStored({
				action: 'st_items_pricings_add_multiple',
				dispatch,
				data: [{ itpr_user_created_uid: userData.user_uid, ...modifiedModel }],
				successHandler
			});
		}
	}

	return (
		<>
			<DialogTitle>
				{t(props.isAdd ? 'DIALOG_ITEMS_PRICINGS_DIALOG_ADD_TITLE' : 'DIALOG_ITEMS_PRICINGS_DIALOG_EDIT_TITLE')}
			</DialogTitle>
			<DialogContent>
				<Form id="itemsPricingsEditDialog" onSubmit={updateLine}>
					<Grid container spacing={2}>
						{/* DATE FROM */}
						<Grid size={{ sm: 6, xs: 12 }}>
							<DateField
								name="itpr_d_from"
								label={t('DETAILS_ITEMS_PRICINGS_FIELD_DATE_FROM')}
								value={model.itpr_d_from}
								isEdit
								sendChange={sendChange}
								required
								validationErrors={{ isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED') }}
							/>
						</Grid>
						{/* DATE TO */}
						<Grid size={{ sm: 6, xs: 12 }}>
							<DateField
								name="itpr_d_to"
								label={t('DETAILS_ITEMS_PRICINGS_FIELD_DATE_TO')}
								value={model.itpr_d_to}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						{/* PRICING GROUP */}
						<Grid size={12}>
							<SearchSelectField
								name="itpr_pric_id"
								label={t('DETAILS_ITEMS_PRICINGS_FIELD_PRICING_GROUP')}
								systable="pricinggroups"
								value={model.itpr_pric_id}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						{/* DESCRIPTION */}
						<Grid size={12}>
							<ModuleSearchField
								name="itpr_comp_uid"
								label={t('DETAILS_ITEMS_PRICINGS_FIELD_COMPANY')}
								module="companies"
								uid={model.itpr_comp_uid}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						{/* MIN QTY */}
						<Grid size={{ sm: 6, xs: 12 }}>
							<NumberTextField
								name="itpr_qty_min"
								label={t('DETAILS_ITEMS_PRICINGS_FIELD_ORDSMINQTY')}
								value={model.itpr_qty_min}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						{/* BASE PRICE */}
						<Grid size={{ sm: 6, xs: 12 }}>
							<CurrencyTextField
								name="itpr_base_price"
								label={t('DETAILS_ITEMS_PRICINGS_FIELD_BASE_PRICE')}
								value={model.itpr_base_price}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						{/* DISCOUNTABLE */}
						<Grid size={12}>
							<BooleanField
								name="itpr_discountable"
								label={t('DETAILS_ITEMS_PRICINGS_FIELD_DISCOUNTABLE')}
								value={model.itpr_discountable}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button type="submit" form="itemsPricingsEditDialog" color="secondary">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
